// Configuration file for Sales Guru Admin Portal
// Values are injected at build time from .env.<CLIENT> files

export const deriveTenantWebsocketUrl = (apiBaseUrl: string | undefined, servicePath: string, localPort: number): string => {
  if (!apiBaseUrl) return `ws://localhost:${localPort}`;

  let apiUrl: URL;
  try {
    apiUrl = new URL(apiBaseUrl);
  } catch {
    throw new Error(`Invalid REACT_APP_API_BASE_URL: ${apiBaseUrl}`);
  }
  if (apiUrl.hostname.endsWith('.backend.salesserver.net')) {
    const tenantHost = apiUrl.hostname.replace('.backend.salesserver.net', '.salesserver.net');
    return `wss://${tenantHost}/${servicePath}/`;
  }
  if (apiUrl.hostname === 'localhost' || apiUrl.hostname === '127.0.0.1' || apiUrl.hostname === '::1') {
    return `ws://localhost:${localPort}`;
  }
  throw new Error(`Cannot derive ${servicePath} websocket URL from deployed API host ${apiUrl.hostname}`);
};

export const config = {
  api: {
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001',
  },
  billing: {
    baseURL: process.env.REACT_APP_BILLING_BASE_URL || 'http://localhost:8002',
  },
  frontend: {
    baseURL: process.env.REACT_APP_FRONTEND_BASE_URL || 'http://localhost:3001',
  },
  websocket: {
    onboardingUrl: process.env.REACT_APP_ONBOARDING_WS_URL
      || deriveTenantWebsocketUrl(process.env.REACT_APP_API_BASE_URL, 'onboarding', 6350),
    /** Optional HTTP base for onboarding REST helpers such as /improve-item.
     *  When absent, the client derives it from onboardingUrl by swapping
     *  ws/wss for http/https and preserving any path prefix such as
     *  /onboarding. */
    onboardingHttpUrl: process.env.REACT_APP_ONBOARDING_HTTP_URL,
    /** deepgram_proxy_service.py — same service `frontend/`'s REACT_APP_DEEPGRAM_URL
     *  points at (port 6331 in dev). Admin portal is a separate build, so it
     *  gets its own env var, but they're expected to resolve to the same proxy. */
    deepgramUrl: process.env.REACT_APP_DEEPGRAM_URL
      || deriveTenantWebsocketUrl(process.env.REACT_APP_API_BASE_URL, 'deepgram', 6331),
  },
  app: {
    name: process.env.REACT_APP_NAME || 'Apexia Labs',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    logLevel: 'INFO',
  },
  auth: {
    tokenRefreshInterval: 5 * 60 * 1000,
    tokenExpiryBuffer: 60,
    /** Firebase UIDs with knowledge-base-only (super admin) access. Must stay in sync with backend/src/config/superAdmins.js */
    superAdminUids: ['xGNSfyYFhGOsFAqqsVXLGywXuW73'] as string[],
  },
  ui: {
    sidebarWidth: 260,
    defaultPageSize: 20,
  },
  tutorial: {
    disabled: process.env.REACT_APP_DISABLE_TUTORIAL === 'true',
  }
};

// Export individual configs for convenience
export const apiConfig = config.api;
export const frontendConfig = config.frontend;
export const appConfig = config.app;
export const authConfig = config.auth;
export const uiConfig = config.ui;
export const tutorialConfig = config.tutorial;
